/**
 * Mastra `UIMessage` ↔ FE `ChatMessage` mapping.
 *
 * Mastra stores one `tool-invocation` part per call (args + result merged).
 * FE renders them as two parts (`tool-call` + `tool-result`) with a `status`
 * field so the UI can show "running" before the result lands. Sub-agents
 * (`agent-*` tools) also need their inner steps flattened into the parent
 * timeline. The mapper converts between the two shapes.
 *
 * - `toChatMessages` — Mastra rows → FE shape. Used on thread load.
 * - `toMastraUserMessage` — build one Mastra `UIMessage` for an outgoing user turn.
 * - `toMastraUIMessages` — FE shape → Mastra rows. Used for resume / replay.
 *
 * `subAgentMessages` (on `tool-call`) is FE-only and dropped on the round-trip back.
 */

import type { UIMessage } from '@ai-sdk/ui-utils';
import { z } from 'zod';

import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { generateUuid } from '@core/utils-legacy';

const MastraToolInvocationPartSchema = z
  .object({
    type: z.literal('tool-invocation'),
    toolInvocation: z.object({
      state: z.enum(['call', 'result', 'partial-call']),
      toolCallId: z.string(),
      toolName: z.string(),
      args: z.unknown(),
      result: z.unknown().optional(),
    }),
  })
  .passthrough();

const MastraTextPartSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
  createdAt: z.number().optional(),
});

const MastraReasoningPartSchema = z.object({
  type: z.literal('reasoning'),
  reasoning: z.string(),
});

const MastraPartSchema = z.union([
  MastraToolInvocationPartSchema,
  MastraTextPartSchema,
  MastraReasoningPartSchema,
  z.object({ type: z.string() }).passthrough(),
]);

const MastraMessageContentSchema = z.object({
  format: z.number().optional(),
  parts: z.array(MastraPartSchema).optional(),
  content: z.string().optional(),
});

const MastraMessageSchema = z.object({
  id: z.string().optional(),
  role: z.enum(['user', 'assistant', 'tool', 'system']),
  content: z.union([z.string(), MastraMessageContentSchema]).optional(),
  parts: z.array(MastraPartSchema).optional(),
  createdAt: z.string().optional(),
});

const SubAgentResultSchema = z.object({
  text: z.string().optional(),
  subAgentThreadId: z.string().optional(),
  subAgentResourceId: z.string().optional(),
  subAgentToolResults: z
    .array(
      z.object({
        toolCallId: z.string(),
        toolName: z.string(),
        args: z.unknown(),
        result: z.unknown(),
      }),
    )
    .optional(),
});

type SubAgentResult = z.infer<typeof SubAgentResultSchema>;

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

type MastraMessage = z.infer<typeof MastraMessageSchema>;

/** Convert Mastra DB rows to FE `ChatMessage[]`. Malformed rows are skipped silently. */
export const toChatMessages = (raw: readonly unknown[]): ChatMessage[] => {
  const out: ChatMessage[] = [];
  for (let i = 0; i < raw.length; i++) {
    const parsed = MastraMessageSchema.safeParse(raw[i]);
    if (!parsed.success) continue;
    const m = parsed.data;
    if (m.role !== 'user' && m.role !== 'assistant') continue;
    const id = m.id ?? generateUuid();
    const parts =
      m.parts && m.parts.length > 0
        ? extractParts({ parts: m.parts }, id)
        : extractParts(m.content, id);
    if (parts.length === 0 && !(m.role === 'assistant' && i === raw.length - 1)) continue;
    out.push({
      id,
      role: m.role,
      parts,
      createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
    });
  }
  return out;
};

function extractParts(content: MastraMessage['content'], messageId: string): ChatPart[] {
  if (content === undefined) return [];
  if (typeof content === 'string') {
    return content ? [{ kind: 'text', messageId, content, done: true }] : [];
  }

  const parts: ChatPart[] = [];
  const seenToolCallIds = new Set<string>();

  for (const p of content.parts ?? []) {
    if (p.type === 'text') {
      const textPart = MastraTextPartSchema.safeParse(p);
      if (textPart.success && textPart.data.text) {
        parts.push({ kind: 'text', messageId, content: textPart.data.text, done: true });
      }
    } else if (p.type === 'reasoning') {
      const reasoningPart = MastraReasoningPartSchema.safeParse(p);
      if (reasoningPart.success && reasoningPart.data.reasoning) {
        parts.push({
          kind: 'reasoning',
          messageId,
          content: reasoningPart.data.reasoning,
          done: true,
        });
      }
    } else if (p.type === 'tool-invocation') {
      const toolPart = MastraToolInvocationPartSchema.safeParse(p);
      if (!toolPart.success) continue;
      const inv = toolPart.data.toolInvocation;
      if (seenToolCallIds.has(inv.toolCallId)) continue;
      seenToolCallIds.add(inv.toolCallId);

      const subAgentMessages = inv.toolName.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)
        ? extractSubAgentMessages(p, inv)
        : undefined;

      parts.push({
        kind: 'tool-call',
        toolCallId: inv.toolCallId,
        name: inv.toolName,
        args: inv.args,
        status: inv.state === 'result' ? 'done' : 'running',
        startedAt: 0,
        ...(subAgentMessages !== undefined ? { subAgentMessages } : {}),
      });
      if (inv.state === 'result') {
        parts.push({
          kind: 'tool-result',
          toolCallId: inv.toolCallId,
          name: inv.toolName,
          payload: inv.result,
        });
      }
    }
  }

  if (parts.length === 0 && content.content) {
    parts.push({ kind: 'text', messageId, content: content.content, done: true });
  }

  return parts;
}

/** Build a Mastra UIMessage for a user-authored text turn. */
export const toMastraUserMessage = (text: string): UIMessage => ({
  id: generateUuid(),
  role: 'user',
  content: text,
  parts: [{ type: 'text', text }],
});

/**
 * System instructions must be sent in plain CoreMessage format ({ role, content }) —
 * adding UIMessage fields (`id`, `parts`) makes the server parse them as UIMessages
 * and reject the run.
 */
export interface MastraSystemMessage {
  role: 'system';
  content: string;
}

export const toMastraSystemMessage = (text: string): MastraSystemMessage => ({
  role: 'system',
  content: text,
});

export type MastraInputMessage = UIMessage | MastraSystemMessage;

type MastraUIPart = NonNullable<UIMessage['parts']>[number];
type MastraToolInvocationPart = Extract<MastraUIPart, { type: 'tool-invocation' }>;

interface MergedTool {
  toolName: string;
  args: unknown;
  result?: unknown;
  hasResult: boolean;
}

function mergeToolStates(parts: readonly ChatPart[]): ReadonlyMap<string, MergedTool> {
  const merged = new Map<string, MergedTool>();
  for (const part of parts) {
    if (part.kind === 'tool-call') {
      const prev = merged.get(part.toolCallId);
      merged.set(part.toolCallId, {
        toolName: part.name,
        args: part.args,
        result: prev?.result,
        hasResult: prev?.hasResult ?? false,
      });
    } else if (part.kind === 'tool-result') {
      const prev = merged.get(part.toolCallId);
      merged.set(part.toolCallId, {
        toolName: prev?.toolName ?? part.name,
        args: prev?.args ?? {},
        result: part.payload,
        hasResult: true,
      });
    }
  }
  return merged;
}

function toMastraToolPart(toolCallId: string, tool: MergedTool): MastraToolInvocationPart {
  const invocation: MastraToolInvocationPart['toolInvocation'] = tool.hasResult
    ? {
        state: 'result',
        toolCallId,
        toolName: tool.toolName,
        args: tool.args,
        result: tool.result,
      }
    : {
        state: 'call',
        toolCallId,
        toolName: tool.toolName,
        args: tool.args,
      };
  return { type: 'tool-invocation', toolInvocation: invocation };
}

function toMastraUIParts(parts: readonly ChatPart[]): MastraUIPart[] {
  const merged = mergeToolStates(parts);
  const firstSeen = new Map<string, number>();
  parts.forEach((part, index) => {
    const id = part.kind === 'tool-call' || part.kind === 'tool-result' ? part.toolCallId : null;
    if (id !== null && !firstSeen.has(id)) firstSeen.set(id, index);
  });

  return parts.flatMap((part, index): MastraUIPart[] => {
    if (part.kind === 'text') return [{ type: 'text', text: part.content }];
    if (part.kind === 'reasoning') {
      return [{ type: 'reasoning', reasoning: part.content, details: [] }];
    }
    if (part.kind !== 'tool-call' && part.kind !== 'tool-result') return [];
    if (firstSeen.get(part.toolCallId) !== index) return [];
    return [toMastraToolPart(part.toolCallId, merged.get(part.toolCallId)!)];
  });
}

/** Convert FE ChatMessage[] to Mastra UIMessage[] (used for resume / replay). */
export const toMastraUIMessages = (msgs: readonly ChatMessage[]): UIMessage[] =>
  msgs.map(m => {
    const partsOut = toMastraUIParts(m.parts);
    return {
      id: m.id,
      role: m.role,
      content: m.parts
        .filter((p): p is Extract<ChatPart, { kind: 'text' }> => p.kind === 'text')
        .map(p => p.content)
        .join(''),
      parts: partsOut,
    };
  });

function extractSubAgentMessages(
  rawPart: unknown,
  inv: { toolCallId: string; result?: unknown },
): ChatMessage[] | undefined {
  const live = (<{ subAgentBuffer?: { messages?: unknown[] } }>rawPart).subAgentBuffer;
  if (live && Array.isArray(live.messages) && live.messages.length > 0) {
    return toChatMessages(live.messages);
  }
  const parsed = SubAgentResultSchema.safeParse(inv.result);
  if (!parsed.success) return undefined;
  return [synthesizeSubAgentMessage(inv.toolCallId, parsed.data)];
}

function synthesizeSubAgentMessage(parentToolCallId: string, result: SubAgentResult): ChatMessage {
  const id = `${parentToolCallId}-sub`;
  const parts: ChatPart[] = [];
  for (const t of result.subAgentToolResults ?? []) {
    parts.push({
      kind: 'tool-call',
      toolCallId: t.toolCallId,
      name: t.toolName,
      args: t.args,
      status: 'done',
      startedAt: 0,
    });
    parts.push({
      kind: 'tool-result',
      toolCallId: t.toolCallId,
      name: t.toolName,
      payload: t.result,
    });
  }
  if (result.text) {
    parts.push({ kind: 'text', messageId: id, content: result.text, done: true });
  }
  return { id, role: 'assistant', parts, createdAt: new Date(0) };
}
